import { Box, Button, TextField, Typography } from '@mui/material'
import { usePlanningStore } from '@planning/AppProvider'
import { DragAndDropZoneWithFilePreview } from '@planning/components/DragAndDropZoneWithFilePreview'
import { LoaderButton } from '@planning/components/LoaderButton'
import { useTranslate } from '@tolgee/react'
import { Loader, useMinimalsTheme } from '@tom-ui/ui'
import { observer } from 'mobx-react-lite'
import { FC, useEffect, useMemo } from 'react'
import { useDropzone } from 'react-dropzone'
import { Helmet } from 'react-helmet-async'
import { useCSVUploadAlerts } from './hooks/useCSVUploadAlerts'
import type { IImportContainerRequest, IYardBlock } from './stores/CSVUploadViewStore'
import { CSVUploadViewStore } from './stores/CSVUploadViewStore'

export type { IImportContainerRequest, IYardBlock }

export interface ICSVUploadPageProps {
  getYardBlocksByNames?: (names: string[]) => Promise<IYardBlock[]>
  stackIn?: (requests: IImportContainerRequest[]) => Promise<void>
}

export const CSVUploadPage: FC<ICSVUploadPageProps> = observer(
  ({ getYardBlocksByNames, stackIn }) => {
    const { t } = useTranslate()
    const theme = useMinimalsTheme()
    const { handleCSVUploadAlert } = useCSVUploadAlerts()

    const { orderItemStore, orderListUploadViewStoreV2, containerJourneyItemStore, tenantStore } =
      usePlanningStore()

    const store = useMemo(
      () =>
        new CSVUploadViewStore(
          orderItemStore,
          orderListUploadViewStoreV2,
          containerJourneyItemStore,
          tenantStore,
          { getYardBlocksByNames, stackIn, onAlert: handleCSVUploadAlert },
        ),
      [orderItemStore, orderListUploadViewStoreV2, containerJourneyItemStore, tenantStore],
    )

    useEffect(() => {
      return () => store.reset()
    }, [store])

    const { getRootProps, getInputProps } = useDropzone({
      maxFiles: 1,
      accept: { 'text/csv': ['.csv'] },
      onDrop: files => store.handleDrop(files),
    })

    return (
      <Box sx={{ minHeight: '100%', bgcolor: theme.palette.grey[100], p: 3 }}>
        <Helmet>
          <title>{t('uploadOrders', 'Upload orders')}</title>
        </Helmet>

        <Typography variant='h4' sx={{ mb: 4, width: '100%', textAlign: 'left' }}>
          {t('uploadOrders', 'Upload orders')}
        </Typography>

        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 1,
            boxShadow: 1,
            p: 3,
            minHeight: '60vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box sx={{ width: '100%', maxWidth: '800px', mb: 3 }}>
            <TextField
              fullWidth
              label={t('licensePlate', 'License Plate')}
              value={store.licensePlate}
              onChange={e => store.setLicensePlate(e.target.value)}
              variant='outlined'
            />
          </Box>

          <Box sx={{ width: '100%', maxWidth: '800px', mb: 4 }}>
            <DragAndDropZoneWithFilePreview
              file={store.file || undefined}
              inputProps={getInputProps()}
              rootProps={getRootProps()}
              setFile={store.handleRemoveFile}
              subtitle={t(
                'weAcceptCsvFilesThatUseOurTemplate',
                'We accept csv files that use our template',
              )}
            />
          </Box>

          {orderListUploadViewStoreV2.parsingStore.uploadFailedReason && (
            <Typography color='error' sx={{ mb: 2 }}>
              {orderListUploadViewStoreV2.parsingStore.uploadFailedReason}
            </Typography>
          )}

          <Box display='flex' alignItems='center' justifyContent='center' gap={2} width='100%'>
            {store.file && (
              <LoaderButton
                onClick={() => store.upsertOrderList()}
                color='primary'
                variant='contained'
                disabled={
                  store.isParsing ||
                  (!store.hasParsedOrders &&
                    !orderListUploadViewStoreV2.parsingStore.uploadFailedReason)
                }
              >
                {store.isParsing ? t('parsingFile', 'Parsing...') : t('upload', 'Upload')}
              </LoaderButton>
            )}

            <Button
              onClick={store.handleDownloadTemplate}
              color='inherit'
              variant='outlined'
              data-cy='download-template-btn'
            >
              {t('downloadTemplate', 'Download template')}
            </Button>
          </Box>
        </Box>
        <Loader show={store.isLoading} />
      </Box>
    )
  },
)
